import { Box, Flex, useToken } from "@chakra-ui/react";
import { Lottie } from "src/components/Lottie";
import { BodyText2 } from "src/atoms/typography";
import {
  dressColors,
  dressDotsColors,
  dress
} from "src/modules/avatar/baby-customization";
import Field from "../Field";
import { ReactComponent as DotColorIcon } from "../../icons/dot-color-icon.svg";

import bear from "./animations/Bear.json";
import body from "./animations/Body.json";
import frog from "./animations/Frog.json";
import nappy from "./animations/Nappy.json";
import bearNappy from "./animations/NappyBear.json";
import frogNappy from "./animations/NappyFrog.json";
import pajamas from "./animations/Pajamas.json";

const dressAnimations = {
  NAPPY: nappy,
  BODY: body,
  PAJAMAS: pajamas,
  BEAR_NAPPY: bearNappy,
  BEAR: bear,
  FROG_NAPPY: frogNappy,
  FROG: frog
};

export default function Dress({ avatar, t, handleAvatarStyleUpdate }) {
  const surfaceElevated1 = useToken("colors", "surfaceElevated1");
  const primary = useToken("colors", "primary");

  const { avatarCustomization } = avatar;
  const { dressColor, clothes } = avatarCustomization;
  const disableColors =
    !clothes || clothes?.includes("FROG") || clothes?.includes("BEAR");

  const colorCss = Object.values(dress).reduce((result, layers) => {
    layers.forEach((layer) => {
      const isDotLayer = layer.includes("Dots");
      const isBaseLayer = layer.includes("Base");
      if (
        (isBaseLayer || isDotLayer) &&
        !layer.includes("Bear") &&
        !layer.includes("Frog")
      ) {
        result =
          result +
          `
& .${layer} {
  fill: ${
    typeof dressColor === "object" && isDotLayer
      ? dressColor[1]
      : typeof dressColor === "object" && isBaseLayer
      ? dressColor[0]
      : isDotLayer
      ? "transparent"
      : dressColor
  };
}
`;
      }
    });
    return result;
  }, "");

  return (
    <Box py="16px" overflow="auto" height="100%" maxHeight="317px">
      <BodyText2 mb="8px" color="highEmphasis">
        {t("avatar.dressType")}
      </BodyText2>
      <Flex
        mb="16px"
        flexWrap="wrap"
        gap="14px"
        css={`
          ${colorCss}
        `}
      >
        {Object.entries(dressAnimations).map(([key, animation]) => {
          return (
            <Field
              key={key}
              onClick={() => handleAvatarStyleUpdate({ clothes: key })}
              isSelected={clothes === key}
            >
              <Lottie
                animationData={animation}
                width="42px"
                height="42px"
                browserProps={{ hasRawData: true }}
              />
            </Field>
          );
        })}
      </Flex>
      <Box
        transition="all 0.3s"
        minHeight={disableColors ? "0px" : "60px"}
        maxHeight={disableColors ? "0px" : "100%"}
        opacity={disableColors ? 0 : 1}
        zIndex={disableColors ? -10 : 0}
      >
        <BodyText2 mb="8px" color="highEmphasis">
          {t("avatar.dressStyle")}
        </BodyText2>

        <Flex ml="5px" flexWrap="wrap" gap="12px">
          {dressColors.map((color) => {
            return (
              <Box
                onClick={() => handleAvatarStyleUpdate({ dressColor: color })}
                key={color}
                width="32px"
                bg={color}
                height="32px"
                borderRadius="100%"
                {...(color === dressColor && {
                  boxShadow: `0 0 0 2pt ${surfaceElevated1},0 0 0 4pt ${primary}`
                })}
              />
            );
          })}
          {dressDotsColors.map((colors) => {
            const [bg, dot] = colors;
            return (
              <Box
                onClick={() => handleAvatarStyleUpdate({ dressColor: colors })}
                key={dot}
                width="32px"
                height="32px"
                borderRadius="100%"
                {...(colors === dressColor && {
                  boxShadow: `0 0 0 2pt ${surfaceElevated1},0 0 0 4pt ${primary}`
                })}
                css={`
                  & .bg {
                    fill: ${bg};
                  }
                  & .dot {
                    fill: ${dot};
                  }
                `}
              >
                <DotColorIcon />
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}
